import { useCallback, useEffect, useMemo, type MutableRefObject } from 'react';
import { Platform } from 'react-native';
import type { NavigationProp } from '@react-navigation/native';
import type { Dispatch, SetStateAction } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { CategoryRecord } from '../../../catalog/domain-api';
import type { PlanEntitlementsRow } from '../../../shell/feature-flags/domain/planEntitlements';
import type { OrganizationBusinessProfile } from '../../../shell/session/domain/businessProfile';
import { usePosStore } from '../../store/usePosStore';
import { usePosOverlayStore } from '../../store/usePosOverlayStore';
import { usePosCheckoutStore } from '../../store/usePosCheckoutStore';
import { isPlaywrightE2E, isPlaywrightE2EBridge } from '../../../shared/playwrightE2e';
import { createBarcodeScanDedupe, looksLikeBarcodeHardwareSubmit } from '../../../shared/product-search/productBarcodeInput';
import type { PosStockSalePolicy } from '../../domain/posTypes';
import { usePosBarcodeScanHandler } from './usePosBarcodeScanHandler';
import { MasterFilterSheetModal } from '../../../shared/filters/MasterFilterSheetModal';
import { usePosProductPress } from './usePosProductPress';
import { usePosRemoteBarcodeImport } from './usePosRemoteBarcodeImport';
import { usePosVariosItem } from './usePosVariosItem';

type Nav = NavigationProp<Record<string, object | undefined>>;

type Params = {
  navigation: Nav;
  canEdit: boolean;
  stockSalePolicy: PosStockSalePolicy;
  businessProfile: OrganizationBusinessProfile | null;
  currentBranchId: string | null | undefined;
  canImportFromCatalog: boolean;
  entitlements: PlanEntitlementsRow | null | undefined;
  userId: string | null | undefined;
  categories: CategoryRecord[];
  setCategories: Dispatch<SetStateAction<CategoryRecord[]>>;
  setCategory: (category: string | 'all' | null) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  setLookupFeedback: (value: string | null) => void;
  reloadCatalog: () => void | Promise<void>;
  allowPosBarcodeEnterSubmit: boolean;
  posBarcodeHardwareSubmitRef: MutableRefObject<() => void>;
};

export function usePosOverlaysPane({
  navigation,
  canEdit,
  stockSalePolicy,
  businessProfile,
  currentBranchId,
  canImportFromCatalog,
  entitlements,
  userId,
  categories,
  setCategories,
  setCategory,
  searchQuery,
  setSearchQuery,
  setLookupFeedback,
  reloadCatalog,
  allowPosBarcodeEnterSubmit,
  posBarcodeHardwareSubmitRef,
}: Params) {
  const addProductToCart = usePosStore((s) => s.addProductToCart);
  const addPackageToCart = usePosStore((s) => s.addPackageToCart);
  const addCustomItemToCart = usePosStore((s) => s.addCustomItemToCart);
  const updateCartLineMenuOptions = usePosStore((s) => s.updateCartLineMenuOptions);
  const updateCartLineVariant = usePosStore((s) => s.updateCartLineVariant);
  const updateCustomCartLine = usePosStore((s) => s.updateCustomCartLine);

  const overlay = usePosOverlayStore(
    useShallow((s) => ({
      variosVisible: s.variosVisible,
      variosName: s.variosName,
      variosAmount: s.variosAmount,
      editingCustomLineId: s.editingCustomLineId,
      openVariosEditor: s.openVariosEditor,
      scannerVisible: s.scannerVisible,
      manualBarcodeVisible: s.manualBarcodeVisible,
    }))
  );

  const setVariosVisible = usePosOverlayStore((s) => s.setVariosVisible);
  const setVariosName = usePosOverlayStore((s) => s.setVariosName);
  const setVariosAmount = usePosOverlayStore((s) => s.setVariosAmount);
  const closeVarios = usePosOverlayStore((s) => s.closeVarios);
  const setScannerVisible = usePosOverlayStore((s) => s.setScannerVisible);
  const setManualBarcodeVisible = usePosOverlayStore((s) => s.setManualBarcodeVisible);
  const setCheckoutVisible = usePosCheckoutStore((s) => s.setCheckoutVisible);

  const playwrightE2eActive = isPlaywrightE2E || isPlaywrightE2EBridge();
  const posBarcodeDedupe = useMemo(() => createBarcodeScanDedupe(), []);

  const productPress = usePosProductPress({
    canEdit,
    stockSalePolicy,
    addProductToCart,
    addPackageToCart,
    updateCartLineMenuOptions,
    updateCartLineVariant,
    currentBranchId,
    navigation,
    setCheckoutVisible,
  });

  const remoteBarcodeImport = usePosRemoteBarcodeImport({
    canImportFromCatalog,
    currentBranchId,
    entitlements,
    userId,
    categories,
    setCategories,
    setCategory,
    setSearchQuery,
    setLookupFeedback,
    handleProductPress: productPress.handleProductPress,
    reloadCatalog,
  });

  const { variosSaving, saveVariousItem, ConfirmModal: variosConfirmModal } = usePosVariosItem({
    canEdit,
    variosName: overlay.variosName,
    variosAmount: overlay.variosAmount,
    editingCustomLineId: overlay.editingCustomLineId,
    closeVarios,
    addCustomItemToCart,
    updateCustomCartLine,
    navigation,
  });

  const { handleScannerResult, scanMissSheetProps } = usePosBarcodeScanHandler({
    navigation,
    currentBranchId,
    setScannerVisible,
    setSearchQuery,
    setCategory,
    setLookupFeedback,
    setVariosVisible,
    handleProductPress: productPress.handleProductPress,
    addProductToCart,
    canImportFromCatalog,
    onRemoteHit: remoteBarcodeImport.handleRemoteBarcodeHit,
  });

  const handlePosBarcodeHardwareSubmit = useCallback(() => {
    if (!allowPosBarcodeEnterSubmit || !canEdit) return;
    const q = searchQuery.trim();
    if (!looksLikeBarcodeHardwareSubmit(q)) return;
    if (!posBarcodeDedupe(q)) return;
    void handleScannerResult(q);
  }, [allowPosBarcodeEnterSubmit, canEdit, searchQuery, posBarcodeDedupe, handleScannerResult]);

  useEffect(() => {
    posBarcodeHardwareSubmitRef.current = handlePosBarcodeHardwareSubmit;
  }, [handlePosBarcodeHardwareSubmit]);

  useEffect(() => {
    if (!playwrightE2eActive || Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }
    window.__VUELTO_E2E_POS_SCAN__ = (rawCode: string) => {
      void handleScannerResult(rawCode);
    };
    return () => {
      delete window.__VUELTO_E2E_POS_SCAN__;
    };
  }, [handleScannerResult, playwrightE2eActive]);

  return {
    handleProductPress: productPress.handleProductPress,
    handlePackagePress: productPress.handlePackagePress,
    handleAddOutOfStockProduct: productPress.handleAddOutOfStockProduct,
    openInventoryForProduct: productPress.openInventoryForProduct,
    stockTapNotice: productPress.stockTapNotice,
    setStockTapNotice: productPress.setStockTapNotice,
    stockTapNoticeCopy: productPress.stockTapNoticeCopy,
    addedFlashProductId: productPress.addedFlashProductId,
    weightProduct: productPress.weightProduct,
    setWeightProduct: productPress.setWeightProduct,
    hourProduct: productPress.hourProduct,
    setHourProduct: productPress.setHourProduct,
    variantPickerProduct: productPress.variantPickerProduct,
    setVariantPickerProduct: productPress.setVariantPickerProduct,
    variantPickerVariants: productPress.variantPickerVariants,
    setVariantPickerVariants: productPress.setVariantPickerVariants,
    pendingVariantId: productPress.pendingVariantId,
    setPendingVariantId: productPress.setPendingVariantId,
    handleVariantSelect: productPress.handleVariantSelect,
    menuOptionsProduct: productPress.menuOptionsProduct,
    setMenuOptionsProduct: productPress.closeMenuOptions,
    menuOptionsGroups: productPress.menuOptionsGroups,
    menuOptionsLoading: productPress.menuOptionsLoading,
    handleMenuOptionsConfirm: productPress.handleMenuOptionsConfirm,
    // Gap 4: edit mode
    openMenuOptionsEditor: productPress.openMenuOptionsEditor,
    openVariantEditor: productPress.openVariantEditor,
    editingMenuOptions: productPress.editingMenuOptions,
    editingLineNote: productPress.editingLineNote,
    remoteImportHit: remoteBarcodeImport.remoteImportHit,
    remoteImportVisible: remoteBarcodeImport.remoteImportVisible,
    remoteImportSaving: remoteBarcodeImport.remoteImportSaving,
    importError: remoteBarcodeImport.importError,
    defaultImportCategoryId: remoteBarcodeImport.defaultImportCategoryId,
    closeRemoteImport: remoteBarcodeImport.closeRemoteImport,
    handleConfirmBarcodeImport: remoteBarcodeImport.handleConfirmBarcodeImport,
    variosVisible: overlay.variosVisible,
    variosName: overlay.variosName,
    variosAmount: overlay.variosAmount,
    editingCustomLineId: overlay.editingCustomLineId,
    openVariosEditor: overlay.openVariosEditor,
    setVariosVisible,
    closeVarios,
    setVariosName,
    setVariosAmount,
    saveVariousItem,
    variosSaving,
    scannerVisible: overlay.scannerVisible,
    manualBarcodeVisible: overlay.manualBarcodeVisible,
    setManualBarcodeVisible,
    handleScannerResult,
    scanMissSheetProps,
    variosConfirmModal,
  };
}
